import React, { FunctionComponent, ReactNode } from 'react'
import * as logos from './logos'
import { COLORS, COLORS_MAP, ROLES, SIZES, LogoNames, ThemedLogoNames, Brand, BRAND_TO_LOGO_MAP } from './logos/constants'
import clsx from 'clsx'
import styles from './style.module.scss'
import { warn } from '../utils/console'
import { useTheme, useThemeClass } from '../Theme'
import { TEST_ID_BY_THEME } from './constants'

import { mapThemedLogoViewBox, getLogoByThemeAndCountry } from './utils'
import type { Country } from '@afterpay/types'

type ColorsType = typeof COLORS
type ColorsKeys = keyof ColorsType
export type LogoColors = ColorsType[ColorsKeys]

type SizesType = typeof SIZES
type SizesKeys = keyof SizesType
export type LogoSizes = SizesType[SizesKeys]

export interface LogoProps {
  name?: LogoNames | ThemedLogoNames
  brand?: Brand
  /**
   * @deprecated color prop is no longer controllable in the Afterpay by Cash logo and will be removed for all logos in a future release.
   */
  color?: LogoColors
  size?: LogoSizes
  title?: string
  description?: string
  country?: Country
}

export interface SvgPropsLogo {
  stroke?: string
  fill?: string
}

export type LogoVariantComponent = FunctionComponent<LogoProps>
type CSSClasses = keyof typeof styles

const Logo: LogoVariantComponent = ({ name, brand, color, size = SIZES.MEDIUM, title, description, country, ...otherProps }) => {
  const themeClass = useThemeClass()
  const { theme } = useTheme()

  if (typeof name === 'undefined' && typeof brand === 'undefined') {
    return null
  }
  const hasAccessibilityProp = typeof title !== 'undefined'
  if (!hasAccessibilityProp) {
    warn('[Core - Logo] Component is used without accessibility attribute. Please include "title" prop to meet AA compliance.')
  }

  const containerClassnames = clsx(styles.container, styles[`logo${size}`] as CSSClasses, styles[`${themeClass}`])
  const ariaHidden = typeof title === 'undefined' && typeof description === 'undefined'
  const logoName = brand ? BRAND_TO_LOGO_MAP[brand] : getLogoByThemeAndCountry(name!, theme.selected, country)
  const viewBox = mapThemedLogoViewBox[logoName]

  const renderTitle = (): ReactNode => {
    if (typeof title === 'undefined') {
      return null
    }
    return <title>{title}</title>
  }

  const renderDescription = (): ReactNode => {
    if (typeof description === 'undefined') {
      return null
    }
    return <desc>{description}</desc>
  }

  const renderLogo = (): ReactNode => {
    const SvgLogo = logos[logoName]
    const svgColor = COLORS_MAP[color as LogoColors]
    return <SvgLogo stroke={svgColor} fill={svgColor || undefined} />
  }

  return (
    <span className={containerClassnames} data-name={name} {...otherProps} data-testid={TEST_ID_BY_THEME[theme.selected]}>
      <svg role={ROLES.IMAGE} aria-hidden={ariaHidden} width='100%' height='100%' viewBox={viewBox}>
        {renderTitle()}
        {renderDescription()}
        {renderLogo()}
      </svg>
    </span>
  )
}

export const BaseLogo = Logo
